// "Android aplikacija": download link and QR code of the APK for the phone, version, install steps;
// below it the Wear OS watch app ("Aplikacija za uro (Galaxy Watch)"): its version, download and a
// short install guide (the full one: docs/SYNOLOGY.md, step 9.10).
import { useEffect, useState } from 'react';
import { ANDROID_APK_PATH, WEAR_APK_PATH, getAndroidVersion, type PublishedApp } from '../../lib/account';
import { androidAppVersion, isAndroidApp } from '../../lib/android';
import { Icon } from '../../components/Icon';
import { svgDataUrl } from '../../components/TotpSetup';
import { useLoaded } from './common';

/** Absolute URL of the APK on this server (what the phone has to open). */
export function apkUrl(): string {
  return new URL(ANDROID_APK_PATH, window.location.origin).href;
}

/** Absolute URL of the watch app's APK (downloaded on the phone, installed on the watch). */
export function wearApkUrl(): string {
  return new URL(WEAR_APK_PATH, window.location.origin).href;
}

/** The phone app version the watch app needs (it takes the watch's recordings over). */
export const WEAR_MIN_PHONE_VERSION = '1.1.0';

/** True when version name `v` ("1.0.2") is at least `min`; unknown formats count as new enough. */
export function versionAtLeast(v: string, min: string): boolean {
  const parse = (x: string) => x.split(/[.-]/).map((p) => Number.parseInt(p, 10));
  const a = parse(v);
  const b = parse(min);
  if (a.some((n) => Number.isNaN(n))) return true;
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const x = a[i] ?? 0;
    const y = b[i] ?? 0;
    if (x !== y) return x > y;
  }
  return true;
}

/**
 * "Aplikacija za uro (Galaxy Watch)": what it does, the published version and, in a browser, the
 * download. The APK has the phone app's package name, so opened on a phone it would replace the
 * phone app: the text says so next to the link, and it is installed with Wear Installer 2.
 */
function WatchApp({ wear, loading, inApp, phoneVersion }: { wear: PublishedApp | null; loading: boolean; inApp: boolean; phoneVersion: string | null }) {
  const url = wearApkUrl();
  return (
    <div className="android-watch" id="android-ura">
      <h3 id="android-ura-title" className="subheading android-watch__title">
        <Icon name="watch" size={20} /> Aplikacija za uro (Galaxy Watch)
      </h3>
      <p className="muted">
        Na uri Samsung Galaxy Watch4 ali novejši posnamete opomnik, vnos v nadzor ali sestanek z enim dotikom (tudi s ploščico ali z oknom
        na številčnici), vidite zamujene, današnje in jutrišnje opomnike in jih označite kot opravljene. Posnetki gredo prek aplikacije na
        telefonu, ki jih naloži na strežnik – na uri ni prijave ne gesla.
      </p>
      <p className="small">
        {loading && !wear
          ? 'Preverjam različico …'
          : wear
            ? `Aplikacija za uro: različica ${wear.versionName}${wear.versionCode ? ` (${wear.versionCode})` : ''}`
            : 'Aplikacija za uro na tem strežniku še ni objavljena.'}
      </p>
      {inApp && phoneVersion && !versionAtLeast(phoneVersion, WEAR_MIN_PHONE_VERSION) && (
        <p className="status-line status-line--warning">
          <Icon name="info" size={20} /> Za uro potrebujete aplikacijo za telefon {WEAR_MIN_PHONE_VERSION} ali novejšo: v aplikaciji odprite
          Nastavitve → Preveri posodobitve.
        </p>
      )}
      {wear && !inApp && (
        <div className="android-watch__download">
          <a className="btn btn--secondary" href={WEAR_APK_PATH} download="baam-transkripcija-ura.apk">
            <Icon name="download" size={20} /> Prenesi aplikacijo za uro
          </a>
          <p className="muted small android-download__url">{url}</p>
          {wear.sha256 && (
            <p className="muted small android-download__hash">
              Kontrolna vsota SHA-256: <code>{wear.sha256}</code>
            </p>
          )}
        </div>
      )}
      {wear && inApp && (
        <p className="muted small android-download__url">
          Datoteko za uro prenesite v brskalniku telefona (Chrome) z naslova {url}
        </p>
      )}
      {wear && (
        <p className="status-line status-line--warning">
          <Icon name="alert" size={20} /> Datoteke za uro ne odpirajte na telefonu: namestila bi se namesto aplikacije za telefon. Na uro jo
          namestite z aplikacijo Wear Installer 2, kot piše v navodilih.
        </p>
      )}
      <details className="android-watch__guide">
        <summary>Navodila za namestitev na uro</summary>
        <ol className="steps">
          <li>
            Ura je povezana s telefonom v aplikaciji Galaxy Wearable, na telefonu je aplikacija Transkripcija {WEAR_MIN_PHONE_VERSION} ali
            novejša in v njej ste prijavljeni. Ura in telefon sta v istem omrežju Wi-Fi.
          </li>
          <li>
            Na uri odprite Nastavitve → O uri → Informacije o programski opremi in večkrat zapored (5- do 7-krat) tapnite Različica
            programske opreme, da se vklopi način za razvijalce.
          </li>
          <li>
            Nastavitve → Možnosti za razvijalce: vklopite odpravljanje napak ADB (ADB debugging) in brezžično odpravljanje napak (Wireless
            debugging). Pod brezžičnim odpravljanjem napak ura pokaže svoj naslov IP.
          </li>
          <li>
            Na telefonu iz Trgovine Google Play namestite brezplačno aplikacijo Wear Installer 2 in v brskalniku telefona prenesite datoteko
            za uro (gumb zgoraj). Datoteke ne odpirajte.
          </li>
          <li>
            V Wear Installer 2 vpišite naslov IP ure in izberite seznanjanje z uro (Pair). Na uri tapnite Seznani novo napravo (Pair new
            device) in v Wear Installer 2 vpišite prikazano kodo in vrata.
          </li>
          <li>
            V Wear Installer 2 izberite preneseno datoteko baam-transkripcija-ura.apk (Custom APK) in jo namestite. Če ura vpraša, ali dovoli
            odpravljanje napak, izberite Dovoli.
          </li>
          <li>
            Na uri odprite Transkripcija in dovolite mikrofon in obvestila. Nato na uri izklopite brezžično odpravljanje napak in
            odpravljanje napak ADB.
          </li>
          <li>
            Ploščico dodate tako, da na številčnici povlečete levo do konca in tapnete +; okno za opomnik pa z dolgim pritiskom na številčnico
            → Prilagodi → izberete prazno mesto → Transkripcija.
          </li>
        </ol>
        <p className="muted small">Posodobitev namestite na enak način. Podrobna navodila in rešitve težav ima skrbnik (navodila za namestitev, korak 9.10).</p>
      </details>
    </div>
  );
}

/** QR code of `text` as an image URL; the generator is only loaded when the section is shown. */
function useQrCode(text: string | null): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!text) return;
    let cancelled = false;
    import('qrcode')
      .then((mod) => {
        // CommonJS package: the functions are on the module or on its default export.
        const qr = ((mod as unknown as { default?: typeof mod }).default ?? mod) as typeof mod;
        return qr.toString(text, { type: 'svg', errorCorrectionLevel: 'M', margin: 2 });
      })
      .then((svg) => {
        if (!cancelled) setUrl(svgDataUrl(svg));
      })
      .catch((err: unknown) => {
        console.warn('[android] QR code failed', err);
      });
    return () => {
      cancelled = true;
    };
  }, [text]);
  return url;
}

export function AndroidSection() {
  const inApp = isAndroidApp();
  const version = useLoaded(getAndroidVersion, 'Različice aplikacije ni bilo mogoče preveriti.');
  const url = inApp ? null : apkUrl();
  const qr = useQrCode(url);
  const published = version.data;

  if (inApp) {
    const v = androidAppVersion();
    return (
      <section className="card" aria-labelledby="android-title" id="android">
        <h2 id="android-title" className="card__title">
          <Icon name="phone" size={22} /> Android aplikacija
        </h2>
        <p>Uporabljate aplikacijo Android{v ? ` (različica ${v})` : ''}.</p>
        <p className="muted">
          S pripomočki na začetnem zaslonu telefona snemate opomnike, nadzore in sestanke z enim dotikom – tudi z zaklenjenim zaslonom.
          Posodobitve in odjavo aplikacije najdete v njenih nastavitvah.
        </p>
        {published && v && published.versionName !== v && (
          <p className="status-line status-line--warning">
            <Icon name="info" size={20} /> Na voljo je nova različica {published.versionName}: v aplikaciji odprite Nastavitve → Preveri posodobitve.
          </p>
        )}
        <WatchApp wear={published?.wear ?? null} loading={version.loading} inApp phoneVersion={v} />
      </section>
    );
  }

  return (
    <section className="card" aria-labelledby="android-title" id="android">
      <h2 id="android-title" className="card__title">
        <Icon name="phone" size={22} /> Android aplikacija
      </h2>
      <p className="muted">
        Z aplikacijo za telefone Android snemate s pripomočki na začetnem zaslonu – z enim dotikom in tudi z zaklenjenim zaslonom. Posnetki se
        naložijo, ko je na voljo povezava.
      </p>
      <div className="android-download">
        <div className="android-download__qr">
          {qr ? <img src={qr} alt="Koda QR s povezavo do aplikacije Android" width={168} height={168} /> : <span className="muted small">Koda QR …</span>}
        </div>
        <div className="android-download__info">
          <a className="btn btn--primary" href={ANDROID_APK_PATH} download="baam-transkripcija.apk">
            <Icon name="download" size={20} /> Prenesi aplikacijo
          </a>
          <p className="small">
            {version.loading && !published
              ? 'Preverjam različico …'
              : published
                ? `Različica ${published.versionName}${published.versionCode ? ` (${published.versionCode})` : ''}`
                : 'Aplikacija na tem strežniku še ni objavljena.'}
          </p>
          <p className="muted small android-download__url">{url}</p>
          {published?.sha256 && (
            <p className="muted small android-download__hash">
              Kontrolna vsota SHA-256: <code>{published.sha256}</code>
            </p>
          )}
        </div>
      </div>
      <h3 className="subheading">Namestitev</h3>
      <ol className="steps">
        <li>Na telefonu skenirajte kodo QR s kamero (ali na telefonu odprite to stran in tapnite »Prenesi aplikacijo«).</li>
        <li>Odprite preneseno datoteko. Če telefon vpraša, dovolite nameščanje aplikacij iz tega vira (brskalnika).</li>
        <li>Odprite aplikacijo Transkripcija in se prijavite s svojim uporabniškim imenom, geslom in kodo iz aplikacije za preverjanje.</li>
        <li>
          Na začetni zaslon dodajte pripomočke: dolg pritisk na prazno mesto → Pripomočki → Transkripcija (Opomnik, Nadzor, Sestanek).
        </li>
      </ol>
      <p className="muted small">Aplikacijo nameščajte samo s tega naslova. Prijava v aplikaciji je vidna med napravami v razdelku »Naprave in seje«.</p>
      <WatchApp wear={published?.wear ?? null} loading={version.loading} inApp={false} phoneVersion={null} />
    </section>
  );
}
